import { HttpClient, HttpHeaders } from "@angular/common/http";
import { inject, Injectable, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import config from "devextreme/core/config";
import { licenseKey } from "devextreme-license";
import { loadMessages, locale } from "devextreme/localization";
import { firstValueFrom, map, Observable, Subject, takeUntil } from "rxjs";

import { Auditor } from "@app/Domain/Models/auditor.model";
import { environment } from "environments/environments";
import { BaseService } from "./base.service";
import { LocalStorageService } from "./local-storage.service";
import { LocaleService } from "./locale.service";

import { IProfile } from "@app/Domain/Models/user.model";

import { MsalService } from "@azure/msal-angular";
import { getBrowserCultureLang, TranslocoService } from "@jsverse/transloco";
import { PrimeNG } from "primeng/config";
import { NotificationService } from "./notification.service";
import { SessionStorageService } from "./session-storage.service";
import { SessionTimeoutService } from "./session-timeout.service";

import ESLocalization from "@assets/translate/dx-rich.es.json";
import PTLocalization from "@assets/translate/dx-rich.pt-BR.json";

@Injectable({
    providedIn: "root"
})
export class AzureAdService extends BaseService implements OnDestroy {
    auditor: Auditor;

    private destroy$ = new Subject<void>();

    private http = inject(HttpClient);
    private sessionStorage = inject(SessionStorageService);
    public localStorageService = inject(LocalStorageService);
    public router = inject(Router);
    public translocoService = inject(TranslocoService);
    public localeService = inject(LocaleService);
    public msalService = inject(MsalService);
    public notification = inject(NotificationService);
    public primengConfig = inject(PrimeNG);
    private sessionTimeout = inject(SessionTimeoutService);

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    async login(): Promise<void> {
        this.msalService.instance
            .handleRedirectPromise()
            .then((authResult) => {
                // Consolida a conta ativa após o redirect (ou a partir das contas em cache).
                // Sem setActiveAccount, getActiveAccount() retorna null e o login redireciona
                // em loop mesmo com sessão válida.
                if (authResult?.account) {
                    this.msalService.instance.setActiveAccount(authResult.account);
                }

                let account = this.msalService.instance.getActiveAccount();
                if (!account) {
                    const accounts = this.msalService.instance.getAllAccounts();
                    if (accounts.length > 0) {
                        account = accounts[0];
                        this.msalService.instance.setActiveAccount(account);
                    }
                }

                if (!account) {
                    this.msalService.instance.loginRedirect();
                } else {
                    // M-06: sessão autenticada confirmada → inicia o timeout absoluto (idempotente).
                    this.sessionTimeout.startSession();
                }
            })
            .catch((error) => {
                this.notification.serviceError(error);
            });
    }

    logout(): void {
        // M-06: encerra o marco de timeout absoluto junto do logout explícito.
        this.sessionTimeout.clearSession();
        this.msalService.instance.logout();
    }

    getUserProfile() {
        return this.http.get<IProfile>(environment.Graph_endpoint);
    }

    getImage(): Observable<Blob> {
        return this.http
            .get(environment.Graph_endpoint_get_photo, {
                responseType: "blob",
                headers: new HttpHeaders({ "Content-Type": "image/jpeg" })
            })
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    async checkUser(culture: string): Promise<Observable<Auditor[]>> {
        return await this.http.get<Auditor[]>(environment.BaseUrl + "Login", this.getHeader(culture));
    }

    async userVerify() {
        try {
            this.translocoService.setAvailableLangs(["pt-BR", "en-US", "es-ES"]);
            const browserLang = getBrowserCultureLang();
            this.translocoService.setActiveLang(browserLang.match(/pt-BR|es-ES|en-US/) ? browserLang : "en-US");

            if (!this.sessionStorage.get("isUserVerified")) {
                const culture = window.navigator.languages ? window.navigator.languages[0] : null;
                const observable = await this.checkUser(culture);
                this.auditor = await firstValueFrom(observable, {
                    defaultValue: null
                });

                if (this.auditor != null) {
                    const idiomaAuditorInterface = this.auditor["idiomaAuditorInterface"];

                    this.sessionStorage.set("auditor", this.auditor);
                    this.sessionStorage.set("isUserVerified", true);
                    this.localStorageService.set("lang", idiomaAuditorInterface);
                    this.localeService.registerCulture(this.auditor.cultura);

                    config({
                        defaultCurrency: this.auditor["codMoeda"],
                        defaultUseCurrencyAccountingStyle: true,
                        forceIsoDateParsing: true,
                        licenseKey: licenseKey
                    });

                    this.setupLanguage(idiomaAuditorInterface);
                } else {
                    if (navigator.language === "pt" || navigator.language === "es") {
                        loadMessages(navigator.language);
                        locale(navigator.language);
                    } else {
                        // loadMessages(enMessages);
                        locale("en");
                    }

                    this.localStorageService.set("lang", browserLang);
                    this.sessionStorage.remove("auditor");
                    this.sessionStorage.set("isUserVerified", false);

                    if (!this.isCurrentRoutePublic()) this.router.navigate(["/login"]);
                }
            } else {
                this.auditor = this.sessionStorage.get("auditor");
                this.setupLanguage(this.auditor.idiomaAuditorInterface);
                this.localeService.registerCulture(this.auditor.cultura);
            }
        } catch (error) {
            if (!this.isCurrentRoutePublic()) {
                this.notification.serviceError(error);
                this.router.navigate(["/login"]);
            }
        }
    }

    private setupLanguage(idioma: string): void {
        const localizationMap = {
            "pt-BR": PTLocalization,
            "es-ES": ESLocalization
        };

        if (localizationMap[idioma]) {
            loadMessages({ [idioma]: localizationMap[idioma] });
            locale(idioma);
        }

        this.translocoService.setActiveLang(idioma);

        this.translocoService
            .selectTranslateObject("primeng")
            .pipe(takeUntil(this.destroy$))
            .subscribe((translations) => {
                this.primengConfig.setTranslation(translations);
            });
    }

    public isCurrentRoutePublic(): boolean {
        const currentPath = window.location.pathname;
        return [
            "WorkingPaperReportPreview",
            "SendDocuments",
            "ReportPreview",
            "ResponseLetter",
            "login",
            "login-failed",
            "not-authorized",
            "not-found",
            "code",
            "**"
        ].some((route) => currentPath.includes(route));
    }
}
